import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import useAuth from '../context/AuthProvider';
import { AppRoutes } from '../helper/appRoutes';
import { CLIENT_HOME_ROLES, hasAnyRole } from '../helper/userRoles';
import {
  StateEntry,
  StateForm,
  getFormUrl,
  getStateEntry,
  getStatesSortedByName,
  getUnavailableReason,
  isExternalForm,
  isFormAvailable,
} from '../helper/createDiligentSearchFormConfig';
import {
  BG_SUBTLE,
  BORDER,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
} from '../components/Elements/AdminHomeDashboardTile';
import { SL_ESTIMATOR_BLUE } from '../helper/surplusLinesTaxEstimatorStyles';

const GUIDANCE_HEADING = 'Create Your Diligent Search Form';

const GUIDANCE_INTRO =
  "Need a diligent search form? We've made it easy. Select the home state below and we'll provide the correct, current form for that state — ready to fill out.";

const GUIDANCE_BULLETS = [
  "Some states require their own specific form, some accept a generic diligent search form, and a few don't require a diligent search at all. Don't worry — once you select the state, we'll show you exactly what applies.",
  "As the surplus lines broker, you are responsible for ensuring that a diligent search of the admitted market is completed before placing a risk with a non-admitted carrier.",
  "Once completed, download and save the form in the insured's policy file. State regulators may request it, so keep it with your records.",
] as const;

const GUIDANCE_CLOSING =
  "For questions about a specific state's requirements, please refer to the State Information Portal or the state for further guidance.";

const FormSlot: React.FC<{ form: StateForm }> = ({ form }) => {
  const available = isFormAvailable(form);
  const url = getFormUrl(form);
  const external = isExternalForm(form);

  return (
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: '8px',
        p: 2,
        mb: 1.5,
        backgroundColor: '#ffffff',
      }}
    >
      <Typography sx={{ fontWeight: 600, color: TEXT_PRIMARY, mb: 1 }}>
        {form.label}
      </Typography>
      {available && url ? (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Link href={url} target="_blank" rel="noopener noreferrer">
            Open form
          </Link>
          {!external && (
            <Link href={url} download>
              Download form
            </Link>
          )}
        </Box>
      ) : (
        <Alert severity="warning" sx={{ mt: 0.5 }}>
          Form unavailable. {getUnavailableReason(form)}
        </Alert>
      )}
      {form.confirmNote && (
        <Typography sx={{ mt: 1, fontSize: '0.875rem', color: TEXT_SECONDARY }}>
          Note: {form.confirmNote}
        </Typography>
      )}
    </Box>
  );
};

const StateOutcome: React.FC<{ entry: StateEntry }> = ({ entry }) => {
  const forms = entry.forms ?? [];

  return (
    <Box sx={{ mt: 3 }}>
      {entry.message && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {entry.message}
        </Alert>
      )}
      {entry.note && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {entry.note}
        </Alert>
      )}
      {forms.map((form) => (
        <FormSlot key={form.label} form={form} />
      ))}
      {entry.category === 'none' && forms.length === 0 && !entry.message && (
        <Alert severity="info">No diligent search form is required for this state.</Alert>
      )}
    </Box>
  );
};

export const CreateDiligentSearchForm: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedCode, setSelectedCode] = useState('');

  const states = useMemo(() => getStatesSortedByName(), []);
  const selectedEntry = selectedCode ? getStateEntry(selectedCode) : undefined;

  const homeRoute = hasAnyRole(user?.userRoles, CLIENT_HOME_ROLES)
    ? AppRoutes.client.home
    : AppRoutes.home.base;

  const handleStateChange = (event: SelectChangeEvent<string>) => {
    setSelectedCode(event.target.value);
  };

  return (
    <Box
      sx={{
        minHeight: { xs: 'calc(100vh - 85px)', md: 'calc(100vh - 250px)' },
        backgroundColor: BG_SUBTLE,
        marginLeft: { xs: '-24px', md: '0px' },
        marginRight: { xs: '-24px', md: '0px' },
        paddingBottom: 3,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(homeRoute)}
          sx={{ mb: 2, color: SL_ESTIMATOR_BLUE }}
        >
          Back to Home
        </Button>

        <Typography
          sx={{
            fontSize: { xs: '1.5rem', md: '1.75rem' },
            fontWeight: 700,
            color: TEXT_PRIMARY,
            mb: 2,
          }}
        >
          Create Diligent Search Form
        </Typography>

        <Box
          sx={{
            border: `1px solid ${BORDER}`,
            borderRadius: '12px',
            backgroundColor: '#ffffff',
            p: { xs: 2, md: 3 },
            mb: 3,
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
          }}
        >
          <Typography
            sx={{
              fontSize: '1.125rem',
              fontWeight: 700,
              color: TEXT_PRIMARY,
              mb: 1.5,
            }}
          >
            {GUIDANCE_HEADING}
          </Typography>
          <Typography sx={{ color: TEXT_SECONDARY, mb: 2, lineHeight: 1.6 }}>
            {GUIDANCE_INTRO}
          </Typography>
          <Typography sx={{ fontWeight: 600, color: TEXT_PRIMARY, mb: 1 }}>
            A few important things to know:
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2 }}>
            {GUIDANCE_BULLETS.map((bullet) => (
              <Typography
                component="li"
                key={bullet.slice(0, 24)}
                sx={{ color: TEXT_SECONDARY, mb: 1, lineHeight: 1.6 }}
              >
                {bullet}
              </Typography>
            ))}
          </Box>
          <Typography sx={{ color: TEXT_SECONDARY, lineHeight: 1.6 }}>
            {GUIDANCE_CLOSING}
          </Typography>
        </Box>

        <FormControl fullWidth sx={{ maxWidth: 480, mb: 2 }}>
          <Select
            labelId="create-diligent-home-state-label"
            id="create-diligent-home-state"
            value={selectedCode}
            onChange={handleStateChange}
            displayEmpty
            inputProps={{ 'aria-label': 'Select the home state' }}
          >
            <MenuItem value="">
              Select the home state
            </MenuItem>
            {states.map((state) => (
              <MenuItem key={state.code} value={state.code}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedEntry && <StateOutcome entry={selectedEntry} />}
      </Container>
    </Box>
  );
};

export default CreateDiligentSearchForm;
